import type { Currency } from '@/services/currency.service';

function findCurrency(currencies: Currency[], code: string | undefined): Currency | undefined {
  if (!code) return undefined;
  const normalized = code.trim().toUpperCase();
  return currencies.find((c) => c.currency_code.trim().toUpperCase() === normalized);
}

/**
 * Mirror backend CurrencyService.getExchangeRate — multiply amount by returned rate.
 */
export function getExchangeRate(
  fromCurrency: string,
  toCurrency: string,
  currencies: Currency[]
): number {
  const from = fromCurrency.trim().toUpperCase();
  const to = toCurrency.trim().toUpperCase();
  if (from === to) return 1;

  const fromCurr = findCurrency(currencies, from);
  const toCurr = findCurrency(currencies, to);
  if (!fromCurr || !toCurr) {
    return 1;
  }

  if (fromCurr.is_base_currency) {
    return 1 / Number(toCurr.exchange_rate);
  }
  if (toCurr.is_base_currency) {
    return Number(fromCurr.exchange_rate);
  }

  return Number(fromCurr.exchange_rate) / Number(toCurr.exchange_rate);
}

export function convertAmount(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  currencies: Currency[]
): number {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  const rate = getExchangeRate(fromCurrency, toCurrency, currencies);
  return n * rate;
}

export function convertToBaseCurrency(
  amount: number,
  fromCurrencyCode: string | undefined,
  currencies: Currency[],
  baseCurrencyCode: string | undefined
): number {
  if (!baseCurrencyCode) {
    return Number(amount) || 0;
  }
  const from = (fromCurrencyCode || baseCurrencyCode).trim().toUpperCase();
  const base = baseCurrencyCode.trim().toUpperCase();
  if (from === base) {
    return Number(amount) || 0;
  }

  const fromCurr = findCurrency(currencies, from);
  if (!fromCurr) {
    if (import.meta.env.DEV) {
      console.warn(`[currencyConversion] Unknown currency "${from}" — using raw amount`);
    }
    return Number(amount) || 0;
  }

  return convertAmount(Number(amount) || 0, from, base, currencies);
}
